import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, ShoppingCart, Search, Filter, Loader2 } from "lucide-react";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon";
import { Product } from "@/types";
import { whatsappService } from "@/lib/whatsapp";
import { useToast } from "@/hooks/use-toast";
import { useOnboarding } from "@/hooks/useOnboarding";
import { OnboardingModal } from "@/components/onboarding/OnboardingModal";
import { Tooltip } from "@/components/onboarding/Tooltip";
import { supabase } from "@/integrations/supabase/client";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from "@/contexts/CartContext";
import { CartIcon } from "@/components/cart/CartIcon";
import { RecommendedForYou } from "@/components/products/ProductRecommendations";

export default function Products() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [likedProducts, setLikedProducts] = useState<Set<string>>(new Set());
  
  // Get category from URL params
  const categoryParam = searchParams.get('category');
  const [selectedCategory, setSelectedCategory] = useState(() => {
    if (!categoryParam) return "all";
    // Match category param to actual category names (case-insensitive)
    const categories = ["Electronics", "Fashion & Clothing", "Home & Garden", "Beauty & Health", "Sports & Outdoors", "Toys & Games", "Books & Media", "Automotive", "Food & Beverages", "Office Supplies", "Jewelry & Accessories", "Pet Supplies"];
    const matched = categories.find(c => c.toLowerCase().replace(/\s*&\s*/g, ' ').includes(categoryParam.toLowerCase().replace(/-/g, ' ')));
    return matched || "all";
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  const { isOnboardingComplete, checkOnboardingStatus } = useOnboarding();

  // Sync URL params with category selection
  useEffect(() => {
    if (categoryParam) {
      const categories = ["Electronics", "Fashion & Clothing", "Home & Garden", "Beauty & Health", "Sports & Outdoors", "Toys & Games", "Books & Media", "Automotive", "Food & Beverages", "Office Supplies", "Jewelry & Accessories", "Pet Supplies"];
      const matched = categories.find(c => c.toLowerCase().replace(/\s*&\s*/g, ' ').includes(categoryParam.toLowerCase().replace(/-/g, ' ')));
      if (matched && matched !== selectedCategory) {
        setSelectedCategory(matched);
      }
    }
  }, [categoryParam]);

  useEffect(() => {
    checkOnboardingStatus().then(() => {
      if (!isOnboardingComplete) {
        setShowOnboarding(true);
      }
    });
  }, [isOnboardingComplete, checkOnboardingStatus]);

  // Fetch products from database
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Use secure RPC to avoid exposing seller_id to anon users
        const { data, error } = await supabase.rpc('get_public_products');

        if (error) throw error;

        if (data) {
          const mappedProducts: (Product & { businessName?: string })[] = data.map((product: any) => ({
            id: product.id,
            sellerId: '', // Hidden from public queries
            title: product.title,
            description: product.description || '',
            price: product.price,
            currency: product.currency || '₦',
            images: product.images || [],
            category: product.category || '',
            stock: product.stock || 0,
            status: product.status as 'active' | 'draft' | 'outOfStock',
            views: product.views || 0,
            likes: product.likes || 0,
            createdAt: new Date(product.created_at),
            updatedAt: new Date(product.created_at),
            businessName: product.business_name || undefined,
          }));
          setProducts(mappedProducts);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        toast({
          title: "Error",
          description: "Failed to load products",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [toast]);

  const categories = ["all", "Electronics", "Fashion & Clothing", "Home & Garden", "Beauty & Health", "Sports & Outdoors", "Toys & Games", "Books & Media", "Automotive", "Food & Beverages", "Office Supplies", "Jewelry & Accessories", "Pet Supplies"];

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          product.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleLike = (productId: string) => {
    const newLikes = new Set(likedProducts);
    if (newLikes.has(productId)) {
      newLikes.delete(productId);
    } else {
      newLikes.add(productId);
    }
    setLikedProducts(newLikes);
  };

  const handleAddToCart = (product: Product) => {
    addToCart({
      id: product.id,
      title: product.title,
      price: product.price,
      image: product.images[0],
      sellerId: product.sellerId
    });
    toast({
      title: "✓ Added to Cart",
      description: `${product.title} added. View cart to checkout.`,
      action: (
        <Button variant="outline" size="sm" onClick={() => navigate('/cart')}>
          View Cart
        </Button>
      ),
    });
  };

  const handleWhatsAppInquiry = async (product: Product) => {
    try {
      const message = `Hi! I'm interested in ${product.title} (${product.currency}${product.price})`;
      
      // Try Web Share API first (native sharing)
      if (navigator.share) {
        await navigator.share({
          title: `Inquiry about ${product.title}`,
          text: message,
        });
        toast({
          title: "Shared successfully!",
          description: "Choose who to send your inquiry to",
        });
      } else {
        // Fallback to WhatsApp direct share
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, "_blank");
        toast({
          title: "WhatsApp opened!",
          description: "Choose who to send your inquiry to",
        });
      }
    } catch (error) {
      console.error('Error sharing:', error);
      if (error.name !== 'AbortError') {
        toast({
          title: "Share failed",
          description: "Failed to share inquiry",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-4">
        <Breadcrumbs />
        
        {/* Personalized Recommendations */}
        <RecommendedForYou 
          title="Recommended For You" 
          subtitle="Based on your likes and browsing history"
          limit={8}
        />
      </div>
      {/* Onboarding Modal */}
      <OnboardingModal 
        open={showOnboarding} 
        onClose={() => setShowOnboarding(false)} 
      />

      {/* Search and Filter Bar */}
      <div className="sticky top-16 z-40 bg-gradient-glass backdrop-blur-lg border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <Tooltip
                  title="Search Products"
                  content="Type keywords to find specific products. You can search by product name or description."
                  showIcon={!isOnboardingComplete}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category === "all" ? "All Categories" : category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Tooltip
                title="Filter by Category"
                content="Choose a specific category to narrow down your product search and find exactly what you need."
                showIcon={!isOnboardingComplete}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Browse Products</h1>
            <p className="text-muted-foreground mt-2">
              {filteredProducts.length} products available
            </p>
          </div>
          <div className="flex items-center gap-4">
            <CartIcon />
            <Tooltip
              title="Product Marketplace"
              content="Browse verified products from trusted sellers. All transactions are protected by our escrow system for your security."
              showIcon={!isOnboardingComplete}
              persistent={!isOnboardingComplete}
            />
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <div className="aspect-square bg-muted animate-pulse" />
                <CardContent className="p-4 space-y-3">
                  <div className="h-5 bg-muted rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
                  <div className="h-7 bg-muted rounded animate-pulse w-1/3" />
                </CardContent>
                <CardFooter className="p-4 pt-0">
                  <div className="h-10 bg-muted rounded animate-pulse w-full" />
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No products found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery ? `No results for "${searchQuery}"` : "No products in this category yet"}
            </p>
            {searchQuery && (
              <Button variant="outline" onClick={() => setSearchQuery('')}>
                Clear Search
              </Button>
            )}
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="aspect-square relative overflow-hidden bg-gray-100">
                <img
                  src={product.images[0]}
                  alt={product.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 cursor-pointer"
                  loading="lazy"
                  onClick={() => navigate(`/products/${product.id}`)}
                />
                {product.stock < 10 && (
                  <div className="absolute top-2 left-2 bg-destructive text-destructive-foreground px-2 py-1 rounded text-xs">
                    Only {product.stock} left
                  </div>
                )}
                <button
                  onClick={() => handleLike(product.id)}
                  aria-label="Add to favorites"
                  className="absolute top-2 right-2 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors"
                >
                  <Heart
                    className={`h-4 w-4 ${
                      likedProducts.has(product.id) ? "fill-red-500 text-red-500" : "text-gray-600"
                    }`}
                  />
                </button>
              </div>
              <CardContent className="p-4">
                <h3 
                  className="font-semibold text-lg mb-1 line-clamp-1 cursor-pointer hover:text-primary transition-colors"
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  {product.title}
                </h3>
                {(product as any).businessName && (
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-1">
                    by {(product as any).businessName}
                  </p>
                )}
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {product.description}
                </p>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-2xl font-bold text-primary">
                    {product.currency}{product.price.toLocaleString()}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      {product.views} views
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      {product.likes} likes
                    </span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="p-4 pt-0 flex gap-2">
                <div className="flex-1 flex gap-2">
                  <Button
                    variant="gradient"
                    className="flex-1"
                    onClick={() => handleAddToCart(product)}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Add to Cart
                  </Button>
                  <Button
                    variant="whatsapp"
                    size="icon"
                    onClick={() => handleWhatsAppInquiry(product)}
                    aria-label="Contact seller on WhatsApp"
                  >
                    <WhatsAppIcon className="h-4 w-4" size={16} />
                  </Button>
                </div>
                {!isOnboardingComplete && (
                  <Tooltip
                    title="Secure Shopping"
                    content="Add items to your cart for secure checkout or contact the seller directly via WhatsApp for inquiries."
                    showIcon={true}
                  />
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
        )}
      </div>
    </div>
  );
}